import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/react";
import { Spinner } from "@heroui/react";
import OperatorTable from "./OperatorTable";
import OperatorForm from "./OperatorForm";

const Operator = () => {
  const today = new Date().toISOString().split("T")[0];
  const [showForm, setShowForm] = useState(false);
  const [operatorData, setOperatorData] = useState([]);
  const [operators, setOperators] = useState([]);
  const [editingOperator, setEditingOperator] = useState(null);

  const {
    isOpen: isMessageOpen,
    onOpen: onMessageOpen,
    onOpenChange: onMessageOpenChange,
    onClose: onMessageClose,
  } = useDisclosure();
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("alert");
  const [modalCallback, setModalCallback] = useState(null);
  const [modalAutoCloseMs, setModalAutoCloseMs] = useState(0);

  useEffect(() => {
    if (isMessageOpen && modalType === "alert" && modalAutoCloseMs > 0) {
      const timeoutId = setTimeout(() => {
        if (typeof modalCallback === "function") {
          const maybeFn = modalCallback();
          if (typeof maybeFn === "function") {
            maybeFn();
          }
        }
        if (typeof onMessageClose === "function") {
          onMessageClose();
        }
        setModalAutoCloseMs(0);
      }, modalAutoCloseMs);
      return () => clearTimeout(timeoutId);
    }
  }, [
    isMessageOpen,
    modalType,
    modalAutoCloseMs,
    modalCallback,
    onMessageClose,
  ]);

  const getOperators = async () => {
    const userId = localStorage.getItem("uid");

    let headers = new Headers();
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const url = `${process.env.REACT_APP_API_HOST}/operators`;

    const requestOptions = {
      method: "GET",
      headers: headers,
    };

    fetch(url, requestOptions)
      .then((response) => {
        if (response.status === 401) {
          console.log("Unauthorized access. Please log in again.");
        } else {
          return response.json();
        }
      })
      .then((result) => {
        if (result.data) {
          setOperatorData(result.data);
        }
      })
      .catch((err) => {
        console.log("Error fetching operators:", err);
      });
  };

  useEffect(() => {
    getOperators();
  }, []);

  const handleEdit = (id) => {
    // alert("Edit clicked for operator ID: " + id);
    setShowForm(true);

    setTimeout(() => {
      const mainContainer = document.querySelector("main");
      if (mainContainer) {
        mainContainer.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);

    const fetchOperatorById = async () => {
      try {
        setLoading(true);

        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append(
          "Authorization",
          "Bearer " + localStorage.getItem("jwt")
        );

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/operators/${id}`,
          {
            method: "GET",
            headers,
          }
        );

        const result = await response.json();

        if (!response.ok || !result || !result.data) {
          alert("Failed to fetch operator data.");
          return;
        }

        const operator = result.data;
        setEditingOperator(operator);
        console.log("Fetched operator:", operator);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching operator:", error);
        alert("Error fetching operator data.");
      }
    };

    fetchOperatorById();
  };
  const handleDelete = (id) => {
    setModalMessage("Are you sure you want to delete this operator?");
    setModalType("confirm");
    setModalCallback(() => () => {
      fetch(`${process.env.REACT_APP_API_HOST}/deleteoperator/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        },
      })
        .then((res) => {
          if (res.status === 401) {
            console.log("Unauthorized access. Please log in again.");
            return null;
          }
          return res.json();
        })
        .then((result) => {
          if (result && result.success) {
            setModalMessage("Operator deleted successfully!");
            setModalType("alert");
            setModalCallback(() => () => {
              getOperators();
            });
            setModalAutoCloseMs(1000);
            onMessageOpen();
          } else {
            setModalMessage("Failed to delete operator");
            setModalType("alert");
            setModalCallback(null);
            onMessageOpen();
          }
        })
        .catch((err) => {
          console.log("Error deleting operator:", err);
          setModalMessage("Error deleting operator");
          setModalType("alert");
          setModalCallback(null);
          onMessageOpen();
        });
    });
    onMessageOpen();
  };

  const fetchOperators = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_HOST}/operators`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setOperators(data.data || []); // assuming API returns data in data.data
      } else {
        console.error("Failed to fetch operators");
      }
    } catch (error) {
      console.error("Error fetching operators:", error);
    }
  };

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Show spinner for 1 second on mount
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner size="lg" variant="spinner" color="warning" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">Operators</h2>
        <Button
          color="primary"
          className="bg-admin_black text-white"
          onClick={() => {
            if (!showForm) {
              // Opening form, so reset editing operator for Add mode
              setEditingOperator(null);
            }
            setShowForm(!showForm);
          }}
        >
          {showForm ? "Cancel" : "Add Operator"}
        </Button>
      </div>

      {showForm && (
        <OperatorForm
          operatorData={operatorData}
          setOperators={setOperators}
          operators={operators}
          getOperators={getOperators}
          fetchOperators={fetchOperators}
          onCancel={() => setShowForm(false)}
          initialData={editingOperator}
        />
      )}
      <OperatorTable
        onEdit={handleEdit}
        onDelete={handleDelete}
        getOperators={getOperators}
        operatorData={operatorData}
      />
      <Modal
        isOpen={isMessageOpen}
        onOpenChange={onMessageOpenChange}
        classNames={{
          backdrop: "bg-black/35",
          wrapper: "flex items-center justify-center min-h-screen",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {modalType === "confirm" && (
                <>
                  <ModalBody className="flex items-start justify-center min-h-[60px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                  <ModalFooter className="-mt-4">
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onPress={() => {
                        if (typeof modalCallback === "function") {
                          const maybeFn = modalCallback();
                          if (typeof maybeFn === "function") {
                            maybeFn();
                          }
                        }
                        onClose();
                      }}
                    >
                      Yes
                    </Button>
                  </ModalFooter>
                </>
              )}

              {modalType === "alert" && (
                <>
                  <ModalBody className="flex items-center justify-center min-h-[80px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
export default Operator;
